import * as moment from 'moment';
import { isString } from 'lodash';
import * as luhn from 'luhn';

type ValidatorReturn = string | null;
type DateInput = string | moment.Moment;

const DATE_FORMAT = 'DD/MM/YYYY';

const isRequired = (value): ValidatorReturn => {
  // An exact false is an answered question
  // checking something isn't false should be another validator
  if (value === false) {
    return null;
  }

  return !value || value.length === 0 ? 'This field is required.' : null;
};

const isNotFalse = (value): ValidatorReturn => {
  if (value) {
    return null;
  }

  return 'This field is required.';
};

const parseDate = (date: DateInput): moment.Moment => {
  if (!isString(date)) {
    return date;
  }

  return moment(date, DATE_FORMAT);
};

const isValidDate = (value: string): ValidatorReturn => {
  return !parseDate(value).isValid() ? 'Invalid Date' : null;
};

const isBefore = (date: DateInput, message: string = null) => (value: string): ValidatorReturn => {
  date = parseDate(date);
  const m = message ? message : 'Must be before ' + date.format(DATE_FORMAT);
  const momentValue = parseDate(value);

  return date.isBefore(momentValue) ? m : null;
};

const isAfter = (date: DateInput, message: string = null) => (value: string): ValidatorReturn => {
  date = parseDate(date);
  const m = message ? message : 'Must be after ' + date.format(DATE_FORMAT);
  const momentValue = parseDate(value);

  return date.isAfter(momentValue) ? m : null;
};

const isFutureDate = (value: string) => {
  const date = moment().startOf('day');
  const momentValue = parseDate(value);

  return date.isSame(momentValue) ? null : isAfter(date, 'Date cannot be in the past')(value);
};

const isOlderThen = (amount: number) => {
  return isBefore(moment().startOf('day').subtract(amount, 'years'), `Traveller must be at least ${amount} years old.`);
};

const isAdult = isBefore(moment().startOf('day').subtract(18, 'years'), 'Traveller must be an adult.');
const isChild = isAfter(moment().startOf('day').subtract(18, 'years'), 'Child must be under 18.');
const isDobAFutureDate =
  isBefore(moment().startOf('day').subtract(0, 'day'), 'Traveller\'s age cannot be in the future.');

// tslint:disable:max-line-length
// Regex from http://emailregex.com/
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isEmail = (value: string): ValidatorReturn => {
  return !emailRegex.test(value) ? 'Invalid Email' : null;
};

const annualLimit = (advancedDays: number) => {
  return isBefore(moment().startOf('day').subtract(-advancedDays, 'day'), `Your policy cannot start more than ${advancedDays} days in the future.`);
};

const singleLimit = (deferredDays: number) => {
  return isBefore(moment().startOf('day').add(deferredDays, 'day'), `Your trip cannot start more than ${deferredDays} days in the future.`);
};

const isMinEndDateStart = (daysFromStartDate: number, startDate: string) => (value: string) => {
  const date = parseDate(value);

  return date.isAfter(parseDate(startDate).startOf('day').add(daysFromStartDate, 'day')) ? null : `Date has to be at least ${daysFromStartDate.toString()} days after the start date`;
};

const singleLimitMonths = (deferredMonths: number) => {
  return isBefore(moment().startOf('months').add(deferredMonths, 'day').format(), `Your trip cannot start more than ${deferredMonths} months in the future.`);
};

const minTripDuration = (value) => isAfter(moment(parseDate(value)).startOf('day').subtract(-1, 'day'), 'Your trip must be longer than 1 days.');

const singleMaxTripDuration = (startDate: string, maxTrip: number) => (endDate) => {
  const sd = parseDate(startDate).startOf('day');
  const ed = parseDate(endDate).endOf('day');
  const duration = ed.diff(sd, 'days');

  if (duration < 1) {
    return 'Your trip must be longer than 1 day.';
  }

  return duration > maxTrip ? 'Trip Duration cannot be greater than ' + maxTrip + '.' : null;
};

const phoneRegex = /^[+]?[0-9]+$/;

const isPhone = (value: string): ValidatorReturn => {
  return !phoneRegex.test(value) || (value).length < 10 ? 'Invalid phone number' : null;
};

const postcodeRegex = /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})/;

const isPostcode = (value: string): ValidatorReturn => {
  return !postcodeRegex.test(value) ? 'Invalid postcode' : null;
};

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*?()\\\-_=+{};:,<.>])[A-Za-z\d$!@#$%^&*?()\\\-_=+{};:,<.>]{8,}/;

const isValidPassword = (value: string): ValidatorReturn => {
  return !passwordRegex.test(value) ? 'Password must be 8 characters long, with at least one lowercase letter, one uppercase letter, one digit and one special character from the following list: !@#$%^&*?()\-_=+{};:,<.>' : null;
};

const isSame = (expects: string, message = 'This value must be the same as %s') => (value: string): ValidatorReturn => {
  return expects === value ? null : message.replace(/%s/i, expects);
};

const isSameEmail = (expects: string, message = 'Email does not match.') => (value: string): ValidatorReturn => {
  return expects === value ? null : message;
};

const doesNotStartWith = (items: string[], stripSpaces = false, message = 'This value cannot start with %s.') => (value: string): ValidatorReturn => {
  let valid = true;

  items.forEach((item) => {
    let checkAgainst = value;

    if (stripSpaces) {
      checkAgainst = value.replace(' ', '');
    }

    if (checkAgainst.toLocaleLowerCase().startsWith(item.toLocaleLowerCase())) {
      valid = false;
    }
  });

  return valid ? null : message.replace(/%s/i, items.join(', '));
};

const isNotLongerThan = (length: number, message = 'This value cannot be longer than %s characters.') =>
  (value: string): ValidatorReturn => {
    return value.length < length ? null : message.replace(/%s/i, length.toString());
  };

const isNotShorterThan = (length: number, message = 'This value cannot be shorter than %s characters.') =>
  (value: string): ValidatorReturn => {
    return value.length > length ? null : message.replace(/%s/i, length.toString());
  };

const isLengthBetween = ([ min, max ]: [ number, number ],
                         message = 'This value should be between %s and %s characters.') =>
  (value: string): ValidatorReturn => {
    const length = value.length;

    return length >= min && length <= max ? null : message.replace(/%s/i, [ min, max ].join(', '));
  };

const isLuhnHappy = (message = 'This does not look like a valid card number') => (value: string): ValidatorReturn => {
  const val = value.replace(/\s/g, '');

  return luhn.validate(val) ? null : message;
};

const expiryDateRegex = /^(0[1-9]|1[0-2])\/?([0-9]{4})$/;
const expiryMonthRegex = /^(0[1-9]|1[0-2])/;
const expiryYearRegex = /^([0-9]{4})$/;

const isValidExpiryDate = (value: string): ValidatorReturn => {
  return !expiryDateRegex.test(value) ? 'Expiry date must be in the format MM/YYYY' : null;
};

const isValidExpiryMonth = (value: string): ValidatorReturn => {
  return !expiryMonthRegex.test(value) ? 'Expiry month must be in the format MM' : null;
};

const isValidExpiryYear = (value: string): ValidatorReturn => {
  return !expiryYearRegex.test(value) ? 'Expiry year must be in the format YYYY' : null;
};

const isNotGreaterThan = (maxNumber: number, message = 'This value cannot be greater than %s.') =>
  (value: number): ValidatorReturn => {
    return value <= maxNumber ? null : message.replace(/%s/i, maxNumber.toString());
  };

const isNotLessThan = (minNumber: number, message = 'This value cannot be less than %s.') =>
  (value: number): ValidatorReturn => {
    return value >= minNumber ? null : message.replace(/%s/i, minNumber.toString());
  };

export {
  isOlderThen,
  isRequired,
  isValidDate,
  isFutureDate,
  isAdult,
  isChild,
  isDobAFutureDate,
  annualLimit,
  singleLimit,
  minTripDuration,
  singleMaxTripDuration,
  isAfter,
  isBefore,
  isEmail,
  isPhone,
  isNotFalse,
  isValidPassword,
  isSame,
  isSameEmail,
  doesNotStartWith,
  isPostcode,
  isNotLongerThan,
  isNotShorterThan,
  isValidExpiryDate,
  isValidExpiryMonth,
  isValidExpiryYear,
  isLengthBetween,
  isLuhnHappy,
  isNotGreaterThan,
  isNotLessThan,
  singleLimitMonths,
  isMinEndDateStart,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/util/validators.ts